import { useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { useMember } from "@/contexts/memberContext";
import { getInitials } from "@/lib/utils";
import { MemberService } from "@/services/MemberService";
import { isElevatedUser } from "@/utils/userPermissions";
import { X } from "lucide-react";
import type { MessageableEntity } from "@/types/MessageableEntity";

/**
 * Already-elevated members are badged "Staff" in the results. In the Practice Manager assignment
 * flows this matters twice over: it stops an admin re-promoting someone who is already staff, and
 * after a promotion the pill is the visible confirmation that the grant actually landed.
 */
const STAFF_ROLES = ["admin", "superadmin", "practice_manager"];
const isStaffEntity = (entity: MessageableEntity): boolean =>
  entity.type === "member" && !!entity.role?.some((r) => STAFF_ROLES.includes(r));

interface MemberSearchProps {
  /** Callback function invoked when a user selects an entity from the search results */
  onSelect: (entity: MessageableEntity) => void;
  /**
   * Array of already selected entities. This is used to filter them out from search results
   * to prevent duplicate selections. The parent component should maintain this state and
   * update it when entities are selected or removed.
   * @example
   * const [selected, setSelected] = useState<MessageableEntity[]>([]);
   * <MemberSearch selectedEntities={selected} onSelect={(e) => setSelected([...selected, e])} />
   */
  selectedEntities?: MessageableEntity[];
  /** Placeholder text for the search input field */
  placeholder?: string;
  /** Label text displayed above the search input */
  label?: string;
  /** When true, filters out group entities from search results, showing only individual members */
  excludeGroups?: boolean;
  /** Additional classNames that affect the input control */
  inputClassName?: string;
  /**
   * Optional custom fetch function for loading entities. When provided, this replaces
   * the default MemberService.getMembersICanMessage() call. Use this to provide
   * context-appropriate member lists (e.g. all active members for booking contexts,
   * or location-filtered members for practice manager assignment).
   */
  fetchEntities?: (searchTerm: string) => Promise<MessageableEntity[]>;
}

/**
 * Reusable member search component for selecting members and groups
 *
 * This component provides a searchable dropdown interface for finding and selecting
 * members or groups that the current user can message. It includes debounced search,
 * automatic filtering of already selected entities, and optional group exclusion.
 *
 * Based on the search functionality from NewConversationDialog
 *
 * @example
 * // Basic usage for selecting members
 * const [selected, setSelected] = useState<MessageableEntity[]>([]);
 * <MemberSearch
 *   onSelect={(entity) => setSelected([...selected, entity])}
 *   selectedEntities={selected}
 * />
 *
 * @example
 * // Exclude groups, only show individual members
 * <MemberSearch
 *   onSelect={handleSelect}
 *   excludeGroups={true}
 *   placeholder="Find a team member..."
 * />
 */
export const MemberSearch = ({
  onSelect,
  selectedEntities = [],
  placeholder = "Search for a member...",
  label,
  excludeGroups = false,
  inputClassName,
  fetchEntities,
}: MemberSearchProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<MessageableEntity[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { member: currentMember } = useMember();
  const canSeeMemberEmails = isElevatedUser(currentMember);

  const containerRef = useRef<HTMLDivElement>(null);
  const memberService = new MemberService();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Create a stable string of selected IDs to avoid dependency issues
  const selectedIds = selectedEntities.map((e) => e.id).join(",");

  // Search for entities with debounce
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    const handler = setTimeout(() => {
      const searchFn = fetchEntities
        ? fetchEntities(searchTerm)
        : memberService.getMembersICanMessage(searchTerm);

      searchFn
        .then((entities) => {
          // Filter out groups if excludeGroups is true
          const filteredEntities = excludeGroups ? entities.filter((e) => e.type === "member") : entities;

          // Filter out already selected entities
          const selectedIdArray = selectedIds.split(",").filter(Boolean);
          const availableEntities = filteredEntities.filter((e) => !selectedIdArray.includes(e.id));

          setSearchResults(availableEntities);
          setIsDropdownOpen(availableEntities.length > 0);
          setIsSearching(false);
        })
        .catch((error) => {
          console.error("Failed to search:", error);
          setSearchResults([]);
          setIsSearching(false);
        });
    }, 300); // debounce 300ms

    return () => clearTimeout(handler);
  }, [searchTerm, selectedIds, excludeGroups]);

  const handleEntitySelect = (entity: MessageableEntity) => {
    setIsDropdownOpen(false);
    setSearchTerm("");
    setSearchResults([]);
    onSelect(entity);
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      {label && <label className="text-sm font-medium">{label}</label>}
      <div className="relative">
        <Input
          placeholder={placeholder}
          className={`w-full bg-white ${inputClassName}`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            if (searchResults.length > 0) {
              setIsDropdownOpen(true);
            }
          }}
        />
        {searchTerm && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">
            <X
              className="w-4 h-4 text-gray-400"
              onClick={() => {
                setSearchTerm("");
                setIsDropdownOpen(false);
              }}
            />
          </div>
        )}
      </div>

      {/* Search Results */}
      <div className="relative">
        {isSearching && <div className="mt-2 text-sm text-gray-500">Searching...</div>}
        {!isSearching && isDropdownOpen && searchResults.length > 0 && (
          <div className="absolute w-full max-h-64 overflow-y-auto z-10 mt-1 bg-white rounded-lg border border-gray-200 shadow-md">
            <ul className="p-2">
              {searchResults.map((entity) => {
                if (entity.type === "member") {
                  const fullName = `${entity.firstName} ${entity.lastName}`;
                  const displayName = entity.organisationName ? `${fullName} (${entity.organisationName})` : fullName;
                  const initials = getInitials(fullName);
                  return (
                    <div key={entity.id} className="mb-1" onClick={() => handleEntitySelect(entity)}>
                      <Item variant="outline" className="w-full bg-gray-50 hover:bg-indigo-100 hover:cursor-pointer">
                        <ItemContent>
                          <div className="flex flex-row items-center space-x-4">
                            <Avatar className="w-10 h-10">
                              <AvatarImage
                                src={
                                  entity.avatar ||
                                  "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                                }
                                alt="User Avatar"
                              />
                              <AvatarFallback>{initials}</AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <ItemTitle className="text-base font-semibold flex items-center gap-2">
                                {displayName}
                                {isStaffEntity(entity) && (
                                  <span
                                    data-testid="member-search-staff-pill"
                                    className="inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-800 rounded-full"
                                  >
                                    Staff
                                  </span>
                                )}
                              </ItemTitle>
                              <ItemDescription className="flex flex-col">
                                {canSeeMemberEmails && <span className="pb-0.5">{entity.email}</span>}
                                {entity.practiceManagerLocations && entity.practiceManagerLocations.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-0.5">
                                    {entity.practiceManagerLocations.map((loc) => (
                                      <span
                                        key={loc.id}
                                        className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded-full"
                                      >
                                        {loc.name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </ItemDescription>
                            </div>
                          </div>
                        </ItemContent>
                      </Item>
                    </div>
                  );
                } else {
                  // Group entity
                  return (
                    <div key={entity.id} className="mb-1" onClick={() => handleEntitySelect(entity)}>
                      <Item variant="outline" className="w-full bg-gray-50 hover:bg-indigo-100 hover:cursor-pointer">
                        <ItemContent>
                          <div className="flex flex-row items-center space-x-4">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-indigo-600 font-semibold text-sm">{entity.memberCount}</span>
                            </div>
                            <div className="flex flex-col">
                              <ItemTitle className="text-base font-semibold">{entity.name}</ItemTitle>
                              <ItemDescription>
                                <span className="text-xs text-gray-500">Group</span>
                              </ItemDescription>
                            </div>
                          </div>
                        </ItemContent>
                      </Item>
                    </div>
                  );
                }
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
